import { useState, useRef, useMemo, useEffect } from 'react';
import { useInitialResults } from '../../hooks/useInitialResults';
import { useCalculatedHeight } from '../../hooks/useCalculatedHeight';
import { MOMIOS, PATROCINADOR } from '../../constantes/constantes';
import CarrouselTeam from '../carrouselTeam/carrouselTeam';
import WithoutResults from '../withoutResults/withoutResults';
import SkeletonSummary from '../skeleton/skeletonSummary';
import AbstractComponent from './abstract';
import CardResults from './cardResults';
import './meta.sass';
import './cardResults.sass';

const GAP = 10;
const PADDING = 30;
const BUTTON_HEIGHT = 50;

/**
 * Componente coordinador para mostrar resultados de búsqueda con manejo de estados
 * 
 * @component
 * @param {Object} props
 * @param {Object|null} props.data - Objeto de respuesta de la API de búsqueda
 * @param {string} props.query - Término de búsqueda ingresado por el usuario
 * @param {boolean} props.isMobile - Indica si es dispositivo móvil
 * @param {boolean} [props.isStreaming=false] - Indica si el resumen está en streaming
 * @param {boolean} [props.notesReady=false] - Indica si las notas ya terminaron de cargar
 * @param {Array} props.dataGames - Datos de juegos para el carrousel
 * @author Valeria Garcia
 */
export default function Results({ data, query, isMobile, isStreaming = false, notesReady = false, dataGames }) {
  const [showAll, setShowAll] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const gridRef = useRef(null);
  const INITIAL_RESULTS = useInitialResults();

  const dataQ = data && Array.isArray(data.hits) ? data.hits : [];
  const resume = data?.resume || '';

  // Reset showAll solo cuando cambian los resultados (hits), no el resumen
  const hitsRef = useRef(dataQ);
  useEffect(() => {
    if (dataQ !== hitsRef.current && dataQ.length !== hitsRef.current.length) {
      setShowAll(false);
      setIsTransitioning(false);
      hitsRef.current = dataQ;
    }
  }, [dataQ]);

  const handleToggle = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setShowAll(!showAll);
      setIsTransitioning(false);
    }, 300);
  };

  const sortedData = useMemo(() =>
    [...dataQ].sort((a, b) => new Date(b.publish_date) - new Date(a.publish_date)),
    [dataQ]
  );

  const visibleData = useMemo(() =>
    showAll ? sortedData : sortedData.slice(0, INITIAL_RESULTS),
    [showAll, sortedData, INITIAL_RESULTS]
  );

  // Calcula la altura basada en las primeras INITIAL_RESULTS cards
  const collapsedHeight = useCalculatedHeight(gridRef, INITIAL_RESULTS, {
    gap: GAP,
    padding: PADDING,
    extraHeight: BUTTON_HEIGHT,
  });

  // Mientras el streaming esté activo o la búsqueda en curso, no consideramos "sin resultados"
  const hasNotes = sortedData.length > 0;
  const showCarrousel = dataGames?.length > 0;
  const hasMoreResults = sortedData.length > INITIAL_RESULTS;

  // El resumen siempre se muestra (skeleton mientras carga, contenido cuando llega).
  // WithoutResults solo se muestra en la zona de notas cuando ya se confirmó que no hay notas.

  // Mobile: sin max-height, muestra todo sin scroll
  // Desktop: altura calculada con scroll cuando está expandido
  const wrapperStyle = isMobile
    ? {}
    : showAll
      ? { maxHeight: collapsedHeight ? `${collapsedHeight + 100}px` : 'auto' }
      : { maxHeight: collapsedHeight ? `${collapsedHeight}px` : 'auto' };

  return (
    <div className="results-container">
      {showCarrousel && (
        <CarrouselTeam
          dataGames={dataGames}
          patrocinador={PATROCINADOR}
          flagMomios={MOMIOS}
        />
      )}

      <div className="container-full-results">
        {data?.resume
          ? <AbstractComponent resume={resume} isMobile={isMobile} isStreaming={isStreaming} query={query} />
          : <div className="skeleton-container"><SkeletonSummary /></div>
        }

        {hasNotes &&
          <section
            className={`list-wrapper-results${showAll ? ' expanded' : ''}`}
            style={wrapperStyle}
          >
            <h2 className='list-wrapper-title'>Te puede interesar:</h2>

            <div ref={gridRef} id="resultados" className="grid-news" role="region" aria-label="Resultados de búsqueda">
              <CardResults data={visibleData} query={query} />
            </div>
            {hasMoreResults && (
              <button
                className={`btn-show-more${showAll ? ' btn-close' : ''}${isTransitioning ? ' transitioning' : ''}`}
                onClick={handleToggle}
                aria-expanded={showAll}
                aria-controls="resultados"
              >
                {showAll ? (
                  <svg className="icon-close" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                ) : (
                  'Mostrar más'
                )}
              </button>
            )}
          </section>
        }
        {/* ) : notesReady ? (
          <WithoutResults query={query} customMessage={data} />
        ) : null} */}
      </div>
    </div>
  );
}